import React            from 'react'
import ReactMarkdown    from 'react-markdown'
import remarkGfm        from 'remark-gfm'

const GH_COLORS = {
  GRAY:   ['#59636e', 'rgba(101, 109, 118, 0.16)'],
  BLUE:   ['#0969da', 'rgba(9, 105, 218, 0.16)'],
  GREEN:  ['#1a7f37', 'rgba(26, 127, 55, 0.16)'],
  YELLOW: ['#9a6700', 'rgba(154, 103, 0, 0.18)'],
  ORANGE: ['#bc4c00', 'rgba(188, 76, 0, 0.16)'],
  RED:    ['#cf222e', 'rgba(207, 34, 46, 0.14)'],
  PINK:   ['#bf3989', 'rgba(191, 57, 137, 0.16)'],
  PURPLE: ['#8250df', 'rgba(130, 80, 223, 0.16)']
}

// Surface the fields CSMs asked for (Status, Tests) at the top of the list.
const FIELD_PRIORITY = { status: 0, tests: 1 }
const fieldRank = name => {
  const key = (name || '').trim().toLowerCase()
  return key in FIELD_PRIORITY ? FIELD_PRIORITY[key] : 2
}

export const ProjectPill = ({ value, color }) => {
  const mapped = GH_COLORS[color]
  if (!mapped) return <span>{value}</span>
  const [text, bg] = mapped
  return <span className="github-card__pill" style={{ color: text, backgroundColor: bg }}>{value}</span>
}

export const formatDate = value => {
  if (!value) return ''
  const date = new Date(value)
  if (isNaN(date)) return ''
  return date.toLocaleString(I18n.locale, {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}

const Avatar = ({ user }) => {
  if (!user) return null
  const label = user.login || '?'
  return (
    <a href={user.html_url} target="_blank" rel="noopener noreferrer" className="github-card__user">
      {user.avatar_url
        ? <img src={user.avatar_url} alt={label} className="github-card__avatar" />
        : <span className="github-card__avatar github-card__avatar--placeholder">{label[0]}</span>}
      <span>{label}</span>
    </a>
  )
}

const GithubIssueDetail = ({ issue, children, showTicket = true }) => {
  if (!issue) return null

  const isOpen     = issue.state === 'open'
  const stateLabel = isOpen ? I18n.t('tickets.github_card.state_open') : I18n.t('tickets.github_card.state_closed')

  const projectFields = (issue.projects || [])
    .flatMap(p => p.fields || [])
    .sort((a, b) => fieldRank(a.name) - fieldRank(b.name))

  return (
    <div className="github-card">
      <div className="github-card__header">
        <span className={`github-card__state github-card__state--${isOpen ? 'open' : 'closed'}`}>
          {stateLabel}
        </span>
        <a href={issue.html_url} target="_blank" rel="noopener noreferrer" className="github-card__issue-link">
          #{issue.number}
        </a>
      </div>
      <h2 className="github-card__title">{issue.title}</h2>
      <div className="github-card__meta">
        {showTicket && issue.ticket &&
          <div className="github-card__meta-row">
            <span className="github-card__meta-label">Ticket Yuman</span>
            <span className="github-card__meta-value">
              <a href={issue.ticket.url} target="_blank" rel="noopener noreferrer">
                #{issue.ticket.id}{issue.ticket.title ? ` — ${issue.ticket.title}` : ''}
              </a>
            </span>
          </div>}

        {projectFields.map((f, i) => (
          <div className="github-card__meta-row" key={`pf-${i}`}>
            <span className="github-card__meta-label">{f.name}</span>
            <span className="github-card__meta-value"><ProjectPill value={f.value} color={f.color} /></span>
          </div>
        ))}

        {issue.issue_type &&
          <div className="github-card__meta-row">
            <span className="github-card__meta-label">{I18n.t('tickets.github_card.type')}</span>
            <span className="github-card__meta-value"><ProjectPill value={issue.issue_type} color="YELLOW" /></span>
          </div>}

        <div className="github-card__meta-row">
          <span className="github-card__meta-label">{I18n.t('tickets.github_card.assignees')}</span>
          <span className="github-card__meta-value">
            {issue.assignees && issue.assignees.length > 0
              ? issue.assignees.map((u, i) => <Avatar key={i} user={u} />)
              : <span className="text-muted">{I18n.t('tickets.github_card.no_assignee')}</span>}
          </span>
        </div>

        <div className="github-card__meta-row">
          <span className="github-card__meta-label">{I18n.t('tickets.github_card.author')}</span>
          <span className="github-card__meta-value"><Avatar user={issue.author} /></span>
        </div>

        {issue.labels && issue.labels.length > 0 &&
          <div className="github-card__meta-row">
            <span className="github-card__meta-label">{I18n.t('tickets.github_card.labels')}</span>
            <span className="github-card__meta-value d-flex a-i-center" style={{ flexWrap: 'wrap', gap: 4 }}>
              {issue.labels.map((l, i) => (
                <span key={i} className="github-card__label" style={{ backgroundColor: `#${l.color || 'ededed'}` }}>
                  {l.name}
                </span>
              ))}
            </span>
          </div>}

        <div className="github-card__meta-row">
          <span className="github-card__meta-label">{I18n.t('tickets.github_card.created_at')}</span>
          <span className="github-card__meta-value">{formatDate(issue.created_at)}</span>
        </div>

        {!isOpen && issue.closed_at &&
          <div className="github-card__meta-row">
            <span className="github-card__meta-label">{I18n.t('tickets.github_card.closed_at')}</span>
            <span className="github-card__meta-value">{formatDate(issue.closed_at)}</span>
          </div>}
      </div>
      {issue.body &&
        <div className="github-card__body markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{issue.body}</ReactMarkdown>
        </div>}
      <div className="github-card__section-title">
        {I18n.t('tickets.github_card.comments')} ({issue.comments_count || 0})
      </div>
      <div className="github-card__comments">
        {issue.comments && issue.comments.length > 0
          ? issue.comments.map(c => (
              <div key={c.id} className="github-card__comment">
                <div className="github-card__comment-head">
                  <Avatar user={c.author} />
                  <span className="github-card__comment-date">{formatDate(c.created_at)}</span>
                </div>
                <div className="github-card__comment-body markdown">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{c.body}</ReactMarkdown>
                </div>
              </div>
            ))
          : <p className="github-card__empty">{I18n.t('tickets.github_card.no_comments')}</p>}
      </div>

      {children}
    </div>
  )
}

export default GithubIssueDetail
